import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGlobalState } from '@/state'
import { formatInTimezone } from '@/utils/date'
import { ArrowRightIcon, CalendarIcon, CashIcon, ExclamationIcon, VolumeUpIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'

const JobsPanel = () => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [ jobs, setJobs ] = useState([])
  const [ canViewDetails, setCanViewDetails ] = useState(false)
  const [ loading, setLoading ] = useState(true)

  useEffect(() => {
    getRequest(`/api/v1/jobs`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { /* handled in hook */ return }
      setJobs(jsonData.jobs || [])
      setCanViewDetails(jsonData.canViewDetails)
    })
  }, [])

  // Stay invisible until there's something worth showing, no empty state, so the
  // dashboard doesn't grow a dead section between job postings.
  if (loading || jobs.length === 0) { return null }

  return <section className='mt-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'>
    <div className="flex items-center justify-between flex-wrap gap-3">
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
        Closing Credits member exclusives
      </h2>
      <span className="text-sm text-slate-500 dark:text-slate-400">
        Closing soonest first
      </span>
    </div>

    <ul className="mt-6 divide-y divide-slate-200 dark:divide-slate-800">
      { jobs.map((job) => (
        <li key={`dashjob${job.id}`} className="py-5 first:pt-0 last:pb-0">
          <Link to={`/jobs/${job.id}`} className="text-base font-bold text-slate-900 dark:text-slate-100 hover:text-clcpurple dark:hover:text-clcpurple-light leading-snug transition-colors">
            {job.name}
          </Link>

          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
            { job.rate && <span className="inline-flex items-center">
              <CashIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
              <span className="font-bold text-slate-700 dark:text-slate-300">{job.rate}</span>
            </span> }
            { job.volume && <span className="inline-flex items-center">
              <VolumeUpIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
              {job.volume}
            </span> }
            <span className="inline-flex items-center">
              <CalendarIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
              Due {formatInTimezone(job.deadlineUtc, currentUser.timezone, 'EEE MMM d, h:mmaaa zzz')}
            </span>
          </div>

          { canViewDetails && <>
            { job.description && <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
              <span dangerouslySetInnerHTML={{ __html: job.description }} />
            </p> }

            <Link
              to={`/jobs/${job.id}`}
              className="mt-3 inline-flex items-center text-sm font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark transition-colors"
            >
              View details
              <ArrowRightIcon className="h-4 w-4 ml-1" aria-hidden="true" />
            </Link>
          </> }
        </li>
      ))}
    </ul>

    { !canViewDetails && <div className="mt-6 rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-300" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <div className='pb-3 text-sm text-yellow-800 dark:text-yellow-200'>
            Full job details are only available to members.
          </div>
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            To read the full brief and apply to these jobs, please <Link to='/subscriptions'>upgrade to the membership</Link>.
          </h3>
        </div>
      </div>
    </div> }
  </section>
}

export default JobsPanel
